import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from 'components/layout/PageHeader';
import { usePaymentVerification } from './hooks/usePaymentVerification';

export const PaymentStatus: React.FC = () => {
  const navigate = useNavigate();
  const { state, hasOnboarding, nextStep, errorMessage } = usePaymentVerification();

  const handleContinue = () => {
    navigate(nextStep);
  };

  const handleBackToPaywall = () => {
    navigate('/paywall');
  };

  return (
    <section className="overflow-x-hidden bg-transparent min-h-screen">
      {/* Back to Website navigation */}
      <PageHeader
        title={
          state === 'loading'
            ? 'Processing Payment...'
            : state === 'success'
            ? 'Payment Successful!'
            : state === 'error'
            ? 'Payment Issue'
            : 'Checkout Status'
        }
        subtitle={
          state === 'loading'
            ? 'Please wait while we confirm your payment and set up your account.'
            : state === 'success'
            ? hasOnboarding
              ? 'Welcome back! You can now access your dashboard.'
              : 'You can now complete your setup and start using Vibemarketing.diy'
            : state === 'error'
            ? 'We encountered an issue with your payment.'
            : 'Checking your payment status...'
        }
        showBackButton={hasOnboarding}
        backButtonText="Back to Dashboard"
        backButtonPath="/dashboard"
        showAvatar={true}
      />

      <div className="max-w-3xl mx-auto px-6">
        {/* Loading State */}
        {state === 'loading' && (
          <div className="flex flex-col items-center justify-center">
            <div className="w-16 h-16 mb-6">
              <svg
                className="animate-spin h-16 w-16 text-orange-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Setting up your account...</h2>
            <p className="text-gray-600 text-center">
              Payment received! We're finalizing your subscription and workspace.
            </p>
          </div>
        )}

        {/* Success State */}
        {state === 'success' && (
          <div className="flex flex-col items-center justify-center">
            {/* Success Icon */}
            <div className="w-20 h-20 mb-6 flex items-center justify-center rounded-full bg-green-100">
              <svg
                className="w-12 h-12 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <button
              onClick={handleContinue}
              className="mt-8 min-h-[48px] px-8 text-[18px] rounded-lg bg-gradient-to-r from-orange-500 to-purple-600 text-white font-medium hover:opacity-90 transition-opacity"
            >
              {hasOnboarding ? 'Go to Dashboard →' : 'Complete Setup →'}
            </button>
          </div>
        )}

        {/* Error State */}
        {state === 'error' && (
          <div className="flex flex-col items-center justify-center">
            {/* Error Icon */}
            <div className="w-20 h-20 mb-6 flex items-center justify-center rounded-full bg-red-100">
              <svg
                className="w-12 h-12 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            <PageHeader
              title="Payment Failed"
              subtitle={errorMessage}
              showBackButton={false}
              showAvatar={false}
            />

            <div className="mt-8 flex gap-4">
              <button
                onClick={handleBackToPaywall}
                className="min-h-[48px] px-8 text-[18px] rounded-lg bg-gradient-to-r from-orange-500 to-purple-600 text-white font-medium hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
              <a
                href="mailto:support@vibemarketing.diy"
                className="min-h-[48px] px-8 text-[18px] rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:border-gray-400 transition-colors flex items-center"
              >
                Contact Support
              </a>
            </div>
          </div>
        )}

        {/* Canceled State */}
        {state === 'canceled' && (
          <div className="flex flex-col items-center justify-center">
            {/* Info Icon */}
            <div className="w-20 h-20 mb-6 flex items-center justify-center rounded-full bg-blue-100">
              <svg
                className="w-12 h-12 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <PageHeader
              title="Checkout Canceled"
              subtitle="You canceled the checkout process. No charges were made."
              showBackButton={false}
              showAvatar={false}
            />

            <button
              onClick={handleBackToPaywall}
              className="mt-8 min-h-[48px] px-8 text-[18px] rounded-lg bg-gradient-to-r from-orange-500 to-purple-600 text-white font-medium hover:opacity-90 transition-opacity"
            >
              Choose a Plan
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
